import { Button, Center, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { extractErrorMessage } from "@equidefi/portals";
import { AttemptsWarning } from "@equidefi/portals/components/AttemptsWarning";
import { SystemDownBanner } from "@equidefi/portals/components/SystemDownBanner";
import { SmartForm } from "@equidefi/portals/components/smart-form";
import { useLogin } from "@equidefi/portals/hooks/useAuthentication";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useLogoutReason } from "@equidefi/portals/hooks/useLogoutReason";
import { BannerAlert, Text } from "@equidefi/ui";
import LoginContainer from "./LoginContainer";

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Please enter a valid email").required("Email is required."),
  password: Yup.string().required("Password is required."),
});

type TFormValues = {
  email: string;
  password: string;
};

const Login = () => {
  useDocumentTitle("Investor Sign In");
  const navigate = useNavigate();
  const toast = useToast();
  const [spinner, showSpinner] = useState(false);
  const location = useLocation();
  const login = useLogin();
  const { logoutReason, logoutMessage } = useLogoutReason({ consumeReason: true });

  const form = useForm<TFormValues>({
    defaultValues: { email: "", password: "" },
    resolver: yupResolver(loginSchema),
    mode: "onTouched",
  });

  const handleLogin = async (data: TFormValues) => {
    showSpinner(true);

    try {
      const email = data.email.toLowerCase().trim();

      await login.mutateAsync({
        email,
        password: data.password,
        type: "investor",
      });

      navigate("/mfa", {
        state: { email, password: data.password, ...location?.state },
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(error, "Incorrect email or password, please try again");
      toast({
        status: "error",
        description: errorMessage,
      });
    } finally {
      showSpinner(false);
    }
  };

  return (
    <LoginContainer>
      <SystemDownBanner />
      {logoutReason && (
        <BannerAlert status="warning" titleLayout="stacked" hideIcon={false} mb="6" p={4} title={logoutMessage} />
      )}
      <AttemptsWarning error={login.error} mb="6" />

      <SmartForm form={form} onSubmit={handleLogin} w="full">
        {({ Input, Password }) => (
          <>
            <Input name="email" placeholder="Email Address" autoFocus mb="5" h="12" />
            <Password name="password" placeholder="Password" borderRadius={5} color="white" h="12" />
            <Button isLoading={spinner} isDisabled={!form.formState.isValid} type="submit" h="12" w="full">
              Continue
            </Button>

            <Center mt="5">
              <Text
                as={Link}
                _hover={{ color: "gray.500" }}
                color="gray.400"
                fontSize="sm"
                to="/forgot"
                textAlign="center">
                Forgot password?
              </Text>
            </Center>
          </>
        )}
      </SmartForm>
    </LoginContainer>
  );
};

export default Login;
